/**
 * Live channel hook — subscribes to a Django Channels websocket route
 * for streaming agent traces, pipeline events, etc. Path is appended to
 * VITE_WS_BASE_URL (defaults to /ws), so dev requests go through the
 * Vite proxy onto Django's ASGI app.
 */
import { useEffect, useRef, useState } from "react";

const WS_BASE = import.meta.env.VITE_WS_BASE_URL || "/ws";

function wsUrl(path: string): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  if (WS_BASE.startsWith("ws")) return `${WS_BASE}${path}`;
  return `${proto}//${location.host}${WS_BASE}${path}`;
}

export interface ChannelEvent {
  type: string;
  ts: string;
  payload: unknown;
}

export function useChannel(path: string | null, opts?: { max?: number }) {
  const [events, setEvents] = useState<ChannelEvent[]>([]);
  const [status, setStatus] = useState<"idle" | "open" | "closed" | "error">("idle");
  const ref = useRef<WebSocket | null>(null);
  const max = opts?.max ?? 200;

  useEffect(() => {
    if (!path) return;
    const ws = new WebSocket(wsUrl(path));
    ref.current = ws;
    ws.onopen = () => setStatus("open");
    ws.onclose = () => setStatus("closed");
    ws.onerror = () => setStatus("error");
    ws.onmessage = (m) => {
      try {
        const parsed = JSON.parse(m.data) as ChannelEvent;
        setEvents((prev) => [...prev.slice(-(max - 1)), parsed]);
      } catch {
        setEvents((prev) => [...prev.slice(-(max - 1)),
          { type: "raw", ts: new Date().toISOString(), payload: m.data }]);
      }
    };
    return () => ws.close();
  }, [path, max]);

  function send(data: unknown) {
    ref.current?.send(typeof data === "string" ? data : JSON.stringify(data));
  }
  function clear() { setEvents([]); }

  return { events, status, send, clear };
}
